// Banner do topo do cardapio publico.
//
// Substitui a faixa de cor solida (`h-28 bg-primary`) que existia antes.
// Tres estados, nessa ordem de prioridade:
//   sem banner  -> a faixa de cor da loja, exatamente como era (zero regressao
//                  pra quem nunca cadastrar banner nenhum);
//   um banner   -> imagem fixa, sem controle nenhum na tela;
//   varios      -> carrossel com setas, bolinhas, arrasto e troca automatica.
//
// A caixa tem proporcao 3,5:1 (7/2) FIXA nos dois canais. E' isso que faz uma
// unica arte servir no computador e no celular: a altura acompanha a largura,
// entao o enquadramento e' o mesmo em 390px e em 1440px — sem segundo corte pro
// lojista e sem imagem esticada.
//
// Banner em VIDEO (23/10/2026, no maximo 1): a capa aparece na hora; o video so
// e' criado quando o slide dele esta na tela, entra por cima quando ja esta
// tocando, e o carrossel espera ele terminar para trocar. Fora da tela pausa.
// Economia de dados / "reduzir movimento" no celular, ou autoplay recusado
// (iPhone em economia de energia): fica so a capa, com a troca de 5 s.

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { podeTocarVideo } from "../../produto-midias/midiasPublicas";
import { bannerImageUrl } from "../lib/bannerImageUrl";

const INTERVALO_MS = 5000;
// Arrasto menor que isso e' rolagem da pagina ou tremida do dedo, nao intencao
// de trocar de banner.
const ARRASTO_MIN_PX = 40;
// Video tocando: teto para nao travar o carrossel (conta do inicio do play)
const VIDEO_MAX_MS = 20000;
// Video que ainda nao comecou a tocar (rede lenta): a capa fica ate isso
const VIDEO_ESPERA_MS = 8000;

export default function CardapioBanner({ banners = [], darkMode = false }) {
  const total = banners.length;
  const [atual, setAtual] = useState(0);
  // Assim que a pessoa toma o controle (seta, bolinha ou arrasto), a troca
  // automatica para de vez. Continuar girando sozinho por baixo da mao de quem
  // esta navegando e' o jeito mais rapido de fazer o usuario perder o banner
  // que ele queria ver.
  const [autoPausado, setAutoPausado] = useState(false);
  const toqueX = useRef(null);
  const caixa = useRef(null);
  const videos = useRef({}); // id do banner -> <video>
  const anterior = useRef(null);
  const [podeVideo] = useState(() => podeTocarVideo());
  // Autoplay recusado (iPhone em modo economia de energia): fica só a capa
  const [bloqueado, setBloqueado] = useState(false);
  const [naTela, setNaTela] = useState(true);
  const [tocando, setTocando] = useState(null); // id do banner em vídeo que já está tocando
  // Vídeos já mostrados uma vez continuam montados (pausados): sair e voltar
  // não baixa de novo, e a troca de slide some com o vídeo em transição suave
  const [montados, setMontados] = useState(() => new Set());
  const idAtual = banners[atual]?.id;
  const usaVideo = podeVideo && !bloqueado;
  const slideVideo = usaVideo && banners[atual]?.media_type === "video";
  const tocandoAtual = slideVideo && tocando === idAtual;

  // Se a lista encolher (banner pausado no painel enquanto a aba esta aberta),
  // o indice pode ficar apontando pra fora do array e a imagem sumiria.
  useEffect(() => {
    setAtual((i) => (total === 0 ? 0 : Math.min(i, total - 1)));
  }, [total]);

  // Um timer por slide: imagem fica 5 s; vídeo troca quando termina (onEnded),
  // com um teto para não travar o carrossel se ele não tocar.
  useEffect(() => {
    if (total < 2 || autoPausado) return undefined;
    const espera = !slideVideo ? INTERVALO_MS : tocandoAtual ? VIDEO_MAX_MS : VIDEO_ESPERA_MS;
    const id = setTimeout(() => setAtual((i) => (i + 1) % total), espera);
    return () => clearTimeout(id);
  }, [total, autoPausado, atual, slideVideo, tocandoAtual]);

  useEffect(() => {
    if (slideVideo && idAtual && !montados.has(idAtual)) setMontados((m) => new Set(m).add(idAtual));
  }, [slideVideo, idAtual, montados]);

  // Banner fora da tela (cliente rolou o cardápio): pausa o vídeo
  const temVideo = usaVideo && banners.some((b) => b.media_type === "video");
  useEffect(() => {
    if (!temVideo || !caixa.current || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(([e]) => setNaTela(e.isIntersecting));
    obs.observe(caixa.current);
    return () => obs.disconnect();
  }, [temVideo]);
  // Só o vídeo do slide da vez toca, e só com o banner na tela. Ao voltar
  // para o slide dele, começa do início.
  useEffect(() => {
    const mudouSlide = anterior.current !== atual;
    anterior.current = atual;
    Object.entries(videos.current).forEach(([id, v]) => {
      if (!v) return;
      if (id === String(idAtual) && naTela) {
        if (mudouSlide) { try { v.currentTime = 0; } catch { /* sem dados ainda */ } }
        v.play().catch((e) => { if (e?.name === "NotAllowedError") setBloqueado(true); });
      } else {
        v.pause();
      }
    });
  }, [naTela, atual, idAtual, montados]);

  // Voltou ao slide do vídeo: a capa aparece de novo até ele tocar
  useEffect(() => { setTocando(null); }, [atual]);

  const fimDoVideo = useCallback(() => {
    if (total > 1 && !autoPausado) setAtual((i) => (i + 1) % total);
  }, [total, autoPausado]);

  const irPara = useCallback((i) => {
    setAutoPausado(true);
    setAtual(((i % total) + total) % total);
  }, [total]);

  const avancar = useCallback((passo) => irPara(atual + passo), [irPara, atual]);

  if (total === 0) {
    // Sem banner cadastrado: a faixa de cor da loja, como sempre foi. No desktop
    // ela e' mais alta porque o cartao de informacoes passaria quase inteiro por
    // fora de uma faixa de 112px — no celular o cartao e' curto e a altura
    // antiga continua certa.
    return <div className="h-28 lg:h-44 bg-primary" />;
  }

  return (
    <div
      ref={caixa}
      className={`relative w-full aspect-[7/2] overflow-hidden ${
        darkMode ? "bg-zinc-800" : "bg-gray-200"
      }`}
      onTouchStart={(e) => { toqueX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        if (toqueX.current == null || total < 2) return;
        const delta = (e.changedTouches[0]?.clientX ?? 0) - toqueX.current;
        toqueX.current = null;
        if (Math.abs(delta) >= ARRASTO_MIN_PX) avancar(delta < 0 ? 1 : -1);
      }}
    >
      {banners.map((banner, i) => {
        const ehVideo = banner.media_type === "video";
        return (
        <div key={banner.id || i} className="contents">
        <img
          src={bannerImageUrl(ehVideo ? banner.poster_url : banner.image_url)}
          alt={banner.title || ""}
          // Todos os slides ficam montados e so a opacidade muda: trocar o
          // `src` faria a proxima imagem piscar em branco enquanto baixa.
          // A capa do vídeo sai quando ele toca: na troca de slide some o
          // vídeo (último quadro), sem piscar a capa (1º quadro) por baixo.
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            i === atual && !(ehVideo && tocando === banner.id) ? "opacity-100" : "opacity-0"
          }`}
          loading={i === 0 ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          aria-hidden={i === atual ? undefined : true}
        />
        {ehVideo && usaVideo && montados.has(banner.id) && (
          <video
            ref={(el) => { videos.current[banner.id] = el; }}
            src={bannerImageUrl(banner.image_url)}
            muted playsInline preload="auto"
            loop={total < 2 || autoPausado}
            onPlaying={() => setTocando(banner.id)}
            onEnded={fimDoVideo}
            onError={() => setBloqueado(true)}
            aria-hidden
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === atual && tocando === banner.id ? "opacity-100" : "opacity-0"
            }`}
          />
        )}
        </div>
        );
      })}

      {total > 1 && (
        <>
          <Seta lado="esquerda" onClick={() => avancar(-1)} />
          <Seta lado="direita" onClick={() => avancar(1)} />

          {/* Bolinhas ACIMA da faixa que o cabecalho cobre — nao no rodape.
              O cartao da loja sobrepoe os 48px de baixo no celular e 64px no
              desktop, entao `bottom-2` deixava a navegacao inteira escondida
              atras dele: existia no DOM e era inalcancavel pelo dedo.

              Alinhadas a DIREITA no celular e centralizadas so no desktop: no
              celular a logo redonda fica no meio do banner, e bolinha branca
              sobre logo branca some. No desktop a logo esta na esquerda, entao
              o centro fica livre. */}
          <div className="absolute right-3 lg:right-auto lg:left-1/2 lg:-translate-x-1/2 bottom-[56px] lg:bottom-[76px] flex items-center gap-1.5 z-10">
            {banners.map((banner, i) => (
              <button
                key={banner.id || i}
                type="button"
                onClick={() => irPara(i)}
                aria-label={`Ver banner ${i + 1} de ${total}`}
                aria-current={i === atual ? "true" : undefined}
                // ring escuro por fora: banner de arte clara (o caso comum —
                // fundo branco com a logo da loja) engolia a bolinha branca.
                className={`h-1.5 rounded-full transition-all ring-1 ring-black/25 ${
                  i === atual ? "w-5 bg-white" : "w-1.5 bg-white/70 hover:bg-white"
                } shadow-sm`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Seta lateral. Centrada na FAIXA VISIVEL do banner, nao no banner inteiro —
 *  centrada no todo, ela cairia parcialmente atras do cartao da loja. */
function Seta({ lado, onClick }) {
  const esquerda = lado === "esquerda";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={esquerda ? "Banner anterior" : "Próximo banner"}
      className={`absolute z-10 -translate-y-1/2 flex items-center justify-center rounded-full
        bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-colors
        w-7 h-7 lg:w-9 lg:h-9
        top-[calc((100%-48px)/2)] lg:top-[calc((100%-64px)/2)]
        ${esquerda ? "left-2 lg:left-3" : "right-2 lg:right-3"}`}
    >
      {esquerda
        ? <ChevronLeft className="w-4 h-4 lg:w-5 lg:h-5" strokeWidth={2.5} />
        : <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" strokeWidth={2.5} />}
    </button>
  );
}
